import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, UploadSimple, ArrowUp, ArrowDown, Trash, X } from "@phosphor-icons/react";
import AuthImage from "@/components/medicao/AuthImage";
import FotosMapa from "@/components/medicao/FotosMapa";
import { errMsg } from "@/lib/medicoes";

const PHOTO_ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.heif,image/*";

function FotoMeta({ f, editable, planilha, onSaved }) {
  const [form, setForm] = useState({ legenda: f.legenda || "", item_numero: f.item_numero ?? "", data_registro: f.data_registro || "",
    local: f.local || "", etapa: f.etapa || "", observacoes: f.observacoes || "" });
  const [dirty, setDirty] = useState(false);
  const set = (k) => (e) => { setForm((s) => ({ ...s, [k]: e.target.value })); setDirty(true); };
  const save = async () => {
    if (!dirty) return;
    try {
      await api.patch(`/fornecedor/medicoes/${f.medicao_id}/arquivos/${f.id}`,
        { ...form, item_numero: form.item_numero === "" ? null : parseInt(form.item_numero, 10) });
      setDirty(false); onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "tce-input w-full px-2.5 py-2 rounded text-xs";
  const meta = f.metadados_originais || {};
  return (
    <div className="space-y-2 flex-1 min-w-0" onBlur={save}>
      <input data-testid={`foto-legenda-${f.id}`} disabled={!editable} value={form.legenda} onChange={set("legenda")} placeholder="Legenda descritiva" className={inp} />
      <div className="grid grid-cols-2 gap-2">
        <select data-testid={`foto-item-${f.id}`} disabled={!editable} value={form.item_numero} onChange={set("item_numero")} className={inp}>
          <option value="">Serviço / item…</option>
          {planilha.map((p) => <option key={p.numero_item} value={p.numero_item}>{p.numero_item} — {p.descricao?.slice(0, 40)}</option>)}
        </select>
        <input type="date" disabled={!editable} value={form.data_registro} onChange={set("data_registro")} className={inp} data-testid={`foto-data-${f.id}`} />
        <input disabled={!editable} value={form.local} onChange={set("local")} placeholder="Local / trecho / estrutura" className={inp} />
        <input disabled={!editable} value={form.etapa} onChange={set("etapa")} placeholder="Etapa / frente" className={inp} />
      </div>
      <textarea disabled={!editable} rows={2} value={form.observacoes} onChange={set("observacoes")} placeholder="Observações técnicas" className={inp} />
      {(meta.data_hora_original || meta.gps_lat != null) && (
        <div className="text-[10px] text-[#94A3B8]" data-testid={`foto-exif-${f.id}`}>
          Metadados do arquivo (não verificados): {meta.data_hora_original || ""} {meta.gps_lat != null && `· GPS ${meta.gps_lat}, ${meta.gps_lon}`}
        </div>
      )}
      {f.localizacao_dispositivo?.lat != null && (
        <div className="text-[10px] text-[#F59E0B]" data-testid={`foto-devloc-${f.id}`}>
          Localização do dispositivo – não verificada: {f.localizacao_dispositivo.lat}, {f.localizacao_dispositivo.lon}
          {f.localizacao_dispositivo.precisao_m ? ` (±${Math.round(f.localizacao_dispositivo.precisao_m)} m)` : ""}
        </div>
      )}
      {dirty && editable && <div className="text-[10px] text-[#F59E0B]">Alterações serão salvas ao sair do campo…</div>}
    </div>
  );
}

const getPosicao = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy }),
    () => resolve(null), { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
});

function Uploader({ medicaoId, tipo, accept, onDone, children, capture, testid }) {
  const ref = useRef(null);
  const [prog, setProg] = useState(null);
  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    const pos = tipo === "foto" && files.length ? await getPosicao() : null;
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]); fd.append("tipo", tipo);
      if (pos) {
        fd.append("device_lat", pos.lat); fd.append("device_lon", pos.lon);
        if (pos.acc) fd.append("device_precisao", pos.acc);
      }
      try {
        await api.post(`/fornecedor/medicoes/${medicaoId}/arquivos`, fd, {
          onUploadProgress: (ev) => setProg({ i: i + 1, n: files.length, pct: Math.round((ev.loaded / (ev.total || 1)) * 100), name: files[i].name }),
        });
      } catch (err) { toast.error(`${files[i].name}: ${errMsg(err, "falha no envio")}`); }
    }
    setProg(null);
    if (files.length) { toast.success(`${files.length} arquivo(s) processado(s)`); onDone?.(); }
  };
  return (
    <>
      <input ref={ref} type="file" multiple={!capture} accept={accept} capture={capture} className="hidden" onChange={onFiles} data-testid={`${testid}-input`} />
      <button type="button" disabled={!!prog} onClick={() => ref.current?.click()} data-testid={testid}
              className="px-4 py-2.5 rounded-md border border-[#334155] hover:border-[#FFDF00] text-sm flex items-center gap-2 disabled:opacity-50">
        {children}
      </button>
      {prog && (
        <div className="w-full" data-testid={`${testid}-progress`}>
          <div className="text-[11px] text-[#94A3B8] mb-1">Enviando {prog.i}/{prog.n} · {prog.name} · {prog.pct}%</div>
          <div className="h-1.5 bg-[#1E293B] rounded"><div className="h-full bg-[#FFDF00] rounded transition-[width]" style={{ width: `${prog.pct}%` }} /></div>
        </div>
      )}
    </>
  );
}

export { Uploader };

export default function FotosManager({ medicaoId, fotos = [], editable, planilha = [], onChange }) {
  const [preview, setPreview] = useState(null);
  const move = async (idx, dir) => {
    const ids = fotos.map((f) => f.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.post(`/fornecedor/medicoes/${medicaoId}/arquivos/reordenar`, { ids }); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (f) => {
    if (!window.confirm("Remover esta fotografia do relatório?")) return;
    try { await api.delete(`/fornecedor/medicoes/${medicaoId}/arquivos/${f.id}`); toast.success("Foto removida"); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="fotos-manager">
      {editable && (
        <div className="flex flex-wrap gap-2 items-center">
          <Uploader medicaoId={medicaoId} tipo="foto" accept={PHOTO_ACCEPT} onDone={onChange} testid="foto-upload-btn">
            <UploadSimple size={16} /> Enviar fotos
          </Uploader>
          <Uploader medicaoId={medicaoId} tipo="foto" accept="image/*" capture="environment" onDone={onChange} testid="foto-camera-btn">
            <Camera size={16} /> Usar câmera
          </Uploader>
          <span className="text-[11px] text-[#64748B]">JPG, PNG, WEBP, HEIC · até 15 MB · original preservado · a localização do aparelho é solicitada ao enviar</span>
        </div>
      )}
      {!fotos.length && <p className="text-sm text-[#94A3B8]">Nenhuma fotografia anexada.</p>}
      <FotosMapa fotos={fotos} />
      {fotos.map((f, idx) => (
        <div key={f.id} data-testid={`foto-card-${idx}`} className="flex flex-col sm:flex-row gap-4 rounded-lg border border-[#334155] p-3 bg-[#0F172A]/40">
          <div className="sm:w-56 shrink-0">
            <div className="text-[10px] uppercase-label mb-1">Foto {idx + 1}</div>
            <AuthImage arquivoId={f.id} alt={f.legenda || f.original_filename}
                       className="w-full h-40 object-cover rounded cursor-zoom-in" onClick={() => setPreview(f)} />
            <div className="text-[10px] text-[#64748B] mt-1 truncate">{f.original_filename}</div>
            {editable && (
              <div className="flex gap-1 mt-2">
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`foto-up-${idx}`} className="p-1.5 rounded border border-[#334155] disabled:opacity-30"><ArrowUp size={12} /></button>
                <button type="button" onClick={() => move(idx, 1)} disabled={idx === fotos.length - 1} data-testid={`foto-down-${idx}`} className="p-1.5 rounded border border-[#334155] disabled:opacity-30"><ArrowDown size={12} /></button>
                <button type="button" onClick={() => remove(f)} data-testid={`foto-del-${idx}`} className="p-1.5 rounded border border-[#EF4444]/40 text-[#EF4444] ml-auto"><Trash size={12} /></button>
              </div>
            )}
          </div>
          <FotoMeta f={f} editable={editable} planilha={planilha} onSaved={onChange} />
        </div>
      ))}
      {preview && (
        <div className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="foto-lightbox">
          <button className="absolute top-4 right-4 text-white" data-testid="foto-lightbox-close"><X size={24} /></button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <AuthImage arquivoId={preview.id} alt={preview.legenda} className="max-h-[80vh] w-auto mx-auto rounded" />
            <div className="text-center text-sm text-white mt-3">{preview.legenda}</div>
          </div>
        </div>
      )}
    </div>
  );
}
